import {
    validateSketchDimensionNominalValue,
    resolveSketchDimensionQuantityKind,
} from '../model/SketchDimension';
import {SketchDimensionReference} from '../model/SketchDimensionReference';
import type {
    CreateSketchDimensionInput,
    SketchDimensionKind,
} from '../SketchDimensionTypes';

/** 各核心类型允许的引用数量范围。 */
const REFERENCE_COUNT_RANGE: Readonly<Record<SketchDimensionKind, {readonly min: number; readonly max: number}>> = {
    'length': {min: 1, max: 1},
    'point-distance': {min: 2, max: 2},
    'point-line-distance': {min: 2, max: 2},
    'line-line-distance': {min: 2, max: 2},
    'angle': {min: 2, max: 2},
    'three-point-angle': {min: 3, max: 3},
    'radius': {min: 1, max: 1},
    'diameter': {min: 1, max: 1},
    /** 真弧长：弧实体 + 圆心 + 两端点（SD-SEL-008 完整引用；弧单选仅数据兼容）。 */
    'arc-length': {min: 1, max: 4},
    'doubled-distance': {min: 2, max: 2},
    'symmetric-angle': {min: 2, max: 2},
    'spline-length': {min: 1, max: 1},
    // 曲线对偏移距离（圆/弧半径差、椭圆半轴差）：源曲线 + 等距曲线。
    'curve-offset-distance': {min: 2, max: 2},
    // 路径总长：端到端重合的单一链（01 §3.2），至少两段；上限为链长度防御值。
    'path-length': {min: 2, max: 64},
};

/**
 * 尺寸引用存在性探测。
 *
 * 由宿主注入（如按草图身份索引查询实体/点是否存在）；返回 false 的引用会被
 * 判为非法创建输入。未注入时跳过存在性校验（纯领域测试场景）。
 */
export type SketchDimensionReferenceExistenceProbe = (
    reference: SketchDimensionReference,
    sketchId: string,
) => boolean;

/**
 * 草图尺寸校验服务。
 *
 * 设计说明：
 * 1. 聚合守卫自身不变量（值域/量纲/引用结构），本服务负责应用层输入校验：
 *    空间范围、引用数量矩阵、引用可构造性与存在性，二者不重复；
 * 2. 校验只抛错或通过，不修改输入、不产生副作用。
 */
export class SketchDimensionValidationService {
    /** 引用存在性探测；未注入时跳过。 */
    private readonly referenceExistenceProbe: SketchDimensionReferenceExistenceProbe | null;

    /**
     * @param referenceExistenceProbe 可选引用存在性探测。
     */
    public constructor(referenceExistenceProbe?: SketchDimensionReferenceExistenceProbe) {
        this.referenceExistenceProbe = referenceExistenceProbe ?? null;
    }

    /**
     * 校验创建输入。
     *
     * @param input 创建输入。
     * @returns 已构造并按选择顺序排序的引用对象列表。
     * @throws 当草图 ID、空间语义、尺寸类型、引用数量、引用数据或引用目标不符合创建契约时抛出错误。
     * @throws 当名义值或业务值换算系数无效时抛出错误。
     */
    public validateCreateInput(input: CreateSketchDimensionInput): readonly SketchDimensionReference[] {
        if (!input.sketchId) {
            throw new Error('创建尺寸缺少 sketchId。');
        }

        const space = input.space ?? 'sketch-2d';

        if (space !== 'sketch-2d') {
            throw new Error(`空间语义 ${space} 属于 3D 扩展位，本轮不支持创建。`);
        }

        const range = REFERENCE_COUNT_RANGE[input.dimensionKind];

        if (!range) {
            throw new Error(`未知尺寸核心类型：${String(input.dimensionKind)}`);
        }
        if (input.references.length < range.min || input.references.length > range.max) {
            throw new Error(
                `尺寸类型 ${input.dimensionKind} 需要 ${range.min}`
                + `${range.max === range.min ? '' : `-${range.max}`} 个引用，`
                + `实际 ${input.references.length} 个。`,
            );
        }

        const references = input.references.map((record) => new SketchDimensionReference(record));
        const orderSet = new Set(references.map((reference) => reference.selectionOrder));

        if (orderSet.size !== references.length) {
            throw new Error('创建尺寸的引用选择顺序重复。');
        }

        if (this.referenceExistenceProbe) {
            references.forEach((reference) => {
                if (!this.referenceExistenceProbe?.(reference, input.sketchId)) {
                    throw new Error(
                        `尺寸引用目标不存在：${reference.signature}（草图 ${input.sketchId}）。`,
                    );
                }
            });
        }

        const quantityKind = resolveSketchDimensionQuantityKind(input.dimensionKind);
        const valueError = validateSketchDimensionNominalValue(quantityKind, input.nominalValue);

        if (valueError) {
            throw new Error(`创建尺寸输入非法：${valueError}`);
        }

        if (input.valueScale !== undefined && (!Number.isFinite(input.valueScale) || input.valueScale <= 0)) {
            throw new Error(`尺寸业务值换算系数必须为正有限数：${String(input.valueScale)}`);
        }

        return [...references].sort((left, right) => left.selectionOrder - right.selectionOrder);
    }
}
